import { createContext, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { KMD_AGENTS, KMD } from "../../data";

const Ctx = createContext({ openAgentSelect: () => {} });
export const useAgentSelect = () => useContext(Ctx);

export const AgentSelectProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <Ctx.Provider value={{ openAgentSelect: () => setOpen(true) }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            data-testid="agent-select-overlay"
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-md px-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="agent-select-modal"
              className="relative w-full max-w-lg rounded-3xl border border-[#bf953f]/30 bg-[#0a192f] p-8 md:p-10 shadow-[0_30px_90px_rgba(0,0,0,0.8)]"
            >
              <button
                onClick={close}
                aria-label="Close"
                data-testid="agent-select-close"
                className="absolute top-5 right-5 text-white/50 hover:text-[#d4af37] transition-colors"
              >
                <X size={20} />
              </button>

              <div className="inline-flex items-center rounded-xl bg-white px-4 py-2.5" data-testid="agent-select-kmd-logo">
                <img src={KMD.logo} alt="KonnectMD" className="h-8 w-auto object-contain" />
              </div>
              <p className="mt-5 font-body text-xs tracking-[0.3em] uppercase text-[#d4af37]">KonnectMD Enrollment</p>
              <h3 className="mt-3 font-serif-display text-3xl md:text-4xl text-white">
                Choose your <span className="text-gold-gradient italic">agent.</span>
              </h3>
              <p className="mt-3 font-body font-light text-sm md:text-base text-white/55 leading-relaxed">
                Select the agent you spoke with so your enrollment is placed with the right agent.
              </p>

              <div className="mt-7 space-y-4">
                {KMD_AGENTS.map((a) => (
                  <a
                    key={a.name}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                    data-testid={`agent-select-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-black/40 p-4 hover:border-[#d4af37]/60 transition-colors duration-300"
                  >
                    <img
                      src={a.photo}
                      alt={a.name}
                      className="h-16 w-16 shrink-0 rounded-full object-cover border-2 border-[#bf953f]/40"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif-display text-xl text-white group-hover:text-[#fcf6ba] transition-colors">{a.name}</p>
                      <p className="font-body text-xs tracking-wide text-white/50">{a.title}</p>
                      <p className="mt-0.5 font-body text-xs text-[#d4af37]/90 truncate">{a.email}</p>
                    </div>
                    <ArrowUpRight
                      size={20}
                      className="text-[#d4af37] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
};
